import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  Building2,
  Landmark,
  Pencil,
  Trash2,
  Filter,
  FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditorRequestHistoryPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null });
  const [deleting, setDeleting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
    account: 'all'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [requestsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/api/creditor-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/creditor-requests/my-assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setRequests(requestsRes.data);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.request) return;
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/api/creditor-requests/${deleteDialog.request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Request deleted successfully');
      setRequests(prev => prev.filter(r => r.id !== deleteDialog.request.id));
      setDeleteDialog({ open: false, request: null });
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reconciled':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="h-3 w-3 mr-1" />Reconciled</Badge>;
      case 'unreconciled':
        return <Badge className="bg-amber-500/20 text-amber-400"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    if (type === 'credit_request') {
      return <Badge className="bg-red-500/20 text-red-400"><TrendingUp className="h-3 w-3 mr-1" />Credit</Badge>;
    }
    return <Badge className="bg-green-500/20 text-green-400"><TrendingDown className="h-3 w-3 mr-1" />Repayment</Badge>;
  };

  // Filter requests
  const filteredRequests = requests.filter(req => {
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch = 
        (req.reference || '').toLowerCase().includes(search) ||
        (req.description || '').toLowerCase().includes(search) ||
        (req.store_name || '').toLowerCase().includes(search) ||
        (req.creditor_name || '').toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Type filter
    if (filters.type !== 'all' && req.request_type !== filters.type) {
      return false;
    }
    
    // Status filter
    if (filters.status !== 'all' && req.reconciliation_status !== filters.status) {
      return false;
    }
    
    // Account filter
    if (filters.account !== 'all') {
      const [storeId, creditorId] = filters.account.split('-');
      if (req.store_id !== storeId || req.creditor_id !== creditorId) {
        return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="request-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/creditor-dashboard')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Request History</h1>
            <p className="text-muted-foreground">View and manage your submitted requests</p>
          </div>
        </div>
        
        <Button onClick={() => navigate('/creditor-requests/new')} data-testid="new-request-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference, description..."
                className="pl-9"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                data-testid="search-input"
              />
            </div>
            
            <Select
              value={filters.type}
              onValueChange={(v) => setFilters(prev => ({ ...prev, type: v }))}
            >
              <SelectTrigger className="w-[150px]" data-testid="type-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="credit_request">Credit</SelectItem>
                <SelectItem value="repayment_request">Repayment</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
            >
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unreconciled">Pending</SelectItem>
                <SelectItem value="reconciled">Reconciled</SelectItem>
              </SelectContent>
            </Select>
            
            {assignments.length > 1 && (
              <Select
                value={filters.account}
                onValueChange={(v) => setFilters(prev => ({ ...prev, account: v }))}
              >
                <SelectTrigger className="w-[200px]" data-testid="account-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {assignments.map(a => (
                    <SelectItem 
                      key={`${a.store_id}-${a.creditor_id}`} 
                      value={`${a.store_id}-${a.creditor_id}`}
                    >
                      {a.store_name} - {a.creditor_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No requests found</p>
              <p className="text-sm">
                {requests.length === 0 
                  ? 'Submit your first request to get started'
                  : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">
                      {formatDate(request.date)}
                    </TableCell>
                    <TableCell>
                      {getTypeBadge(request.request_type)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-3 w-3 text-muted-foreground" />
                        <span>{request.store_name}</span>
                        <span className="text-muted-foreground">•</span>
                        <Landmark className="h-3 w-3 text-muted-foreground" />
                        <span>{request.creditor_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {request.reference || '-'}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${request.request_type === 'credit_request' ? 'text-red-400' : 'text-green-400'}`}>
                      {formatCurrency(request.amount)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.reconciliation_status)}
                    </TableCell>
                    <TableCell className="text-right">
                      {request.reconciliation_status === 'unreconciled' && (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/creditor-requests/edit/${request.id}`)}
                            data-testid={`edit-${request.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteDialog({ open: true, request })}
                            data-testid={`delete-${request.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {filteredRequests.length > 0 && (
        <div className="flex justify-end gap-6 text-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-red-400" />
            <span className="text-muted-foreground">Total Credits:</span>
            <span className="font-semibold text-red-400">
              {formatCurrency(
                filteredRequests
                  .filter(r => r.request_type === 'credit_request')
                  .reduce((sum, r) => sum + (r.amount || 0), 0)
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-green-400" />
            <span className="text-muted-foreground">Total Repayments:</span>
            <span className="font-semibold text-green-400">
              {formatCurrency(
                filteredRequests
                  .filter(r => r.request_type === 'repayment_request')
                  .reduce((sum, r) => sum + (r.amount || 0), 0)
              )}
            </span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, request: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteDialog.request?.request_type === 'credit_request' ? 'credit request' : 'repayment'} for {formatCurrency(deleteDialog.request?.amount)}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, request: null })}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
